import * as React from 'react'
import { useInViewport } from 'react-in-viewport'

import { useProductDescriptionAccordionTransformer } from '@thg-commerce/enterprise-components'
import {
  EnterpriseContext,
  useI18n,
  useSiteConfig,
  useSiteDefinition,
  useTransmit,
} from '@thg-commerce/enterprise-core'
import { getColumboEventData } from '@thg-commerce/enterprise-metrics'
import {} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Product/ProductContent'
import { ProductDescriptionAccordion } from '@thg-commerce/gravity-patterns'
import { ProductAccordionThemeInterface } from '@thg-commerce/gravity-patterns/ProductDescriptionAccordion/types'
import { useTheme } from '@thg-commerce/gravity-patterns/theme'

import { useProductPage } from '../../context'

import { SubTitle, Title } from './styles'

interface ProductDescriptionAccordionRendererProps {
  isDesktop?: boolean
  theme?: ProductAccordionThemeInterface
}

const getProductInfoTitle = (productInfoTitle: string, hideTitle?: boolean) =>
  productInfoTitle && !hideTitle

const selectedSku = (hasSelectedVariant, selectedVariant, product) =>
  hasSelectedVariant ? selectedVariant?.sku : product?.sku

const hasSelectedVariant = (selectedVariant, product) =>
  selectedVariant && product?.sku !== selectedVariant?.sku

export const ProductDescriptionAccordionRenderer = (
  props: ProductDescriptionAccordionRendererProps,
) => {
  const i18n = useI18n()
  const {
    showProductDetails,
    showProductInfoDetails,
    enablePromoMessageOnProductDescription,
  } = useSiteConfig()
  const theme = useTheme()
  const { product, selectedVariant } = useProductPage()
  const useContentTransformer = useProductDescriptionAccordionTransformer()
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { inViewport, enterCount } = useInViewport(
    containerRef,
    {},
    { disconnectOnLeave: true },
  )
  const transmit = useTransmit()
  const { defaultLocale, subsite, siteId, channel } = useSiteDefinition()
  const EnterpriseCtx = React.useContext(EnterpriseContext)
  const propertyArgs = React.useMemo(
    () => ({
      defaultLocale,
      subsite,
      siteId,
      channel,
      customerLocation: EnterpriseCtx.currentLocation,
    }),
    [defaultLocale, subsite, siteId, channel, EnterpriseCtx.currentLocation],
  )

  const descriptionContent = hasSelectedVariant(selectedVariant, product)
    ? selectedVariant?.product?.description
    : product?.description

  const i18nText = {
    productInfoTitle: i18n('product.description.accordion.title'),
    productInfoUseAccordion: i18n('product.description.accordion.useaccordion'),
  }

  const { customTopItems, customBottomItems } = useContentTransformer(
    descriptionContent || null,
    props.isDesktop || false,
    selectedSku(hasSelectedVariant, selectedVariant, product),
  )

  const formattedItems =
    descriptionContent?.items.map((item) => ({
      ...item,
      title: i18n(`product.content.${item.titleKey}.title.text`),
    })) || []

  const formattedDetails =
    descriptionContent?.details.map((item) => ({
      ...item,
      title: i18n(`product.content.${item.titleKey}.title.text`),
    })) || undefined

  const transmitColumboEvent = React.useCallback(
    (eventType: 'focus' | 'click') =>
      transmit({
        type: 'columbo',
        payload: getColumboEventData({
          propertyArgs,
          argumentsObj: {
            '0': 'ProductDescription',
            '1': eventType,
          },
          requestArgs: {
            client_timestamp: Math.round(Date.now() / 1000),
            url: window.location.href,
          },
          eventData: {
            type: eventType,
            subtype: 'productDescription',
            contents: [],
          },
          nonce: EnterpriseCtx.metricNonce,
        }),
      }),
    [EnterpriseCtx.metricNonce, propertyArgs, transmit],
  )
  React.useEffect(() => {
    if (inViewport && enterCount === 1) {
      transmitColumboEvent('focus')
    }
  }, [inViewport, enterCount, transmitColumboEvent])

  const productInfoTitle = getProductInfoTitle(
    i18nText.productInfoTitle,
    props.theme?.accordion?.hideTitle,
  )

  return (
    <div ref={containerRef}>
      {showProductInfoDetails && (
        <React.Fragment>
          {productInfoTitle && (
            <Title hasBottomPadding={!i18nText.productInfoUseAccordion}>
              {i18nText.productInfoTitle}
            </Title>
          )}
          {i18nText.productInfoUseAccordion && (
            <SubTitle>{i18nText.productInfoUseAccordion}</SubTitle>
          )}
        </React.Fragment>
      )}
      <ProductDescriptionAccordion
        items={formattedItems}
        details={{
          title: i18n('product.content.productdetails.title.text'),
          items: formattedDetails,
          display: showProductDetails,
          displayDetailsAsList:
            theme.patterns.productDescriptionAccordion.details
              .showProductDetailsAsList,
        }}
        customTopItems={customTopItems}
        customBottomItems={customBottomItems}
        theme={props.theme}
        enablePromoMessageOnProductDescription={
          enablePromoMessageOnProductDescription
        }
        onOpenAccordion={() => {
          transmitColumboEvent('click')
        }}
      />
    </div>
  )
}
